/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { Stack, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo } from 'react';
import { useNotifications } from '@/data/Content/Notifications';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Linkable } from '@/components/blocks/Linkable';
import {
	linkTitleSX,
	tileDescription2SX,
} from '@/components/content/ProductDetails/styles/attributes';
import { useSettings, useSingleSettings } from '@/data/Settings';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { NotifyAvailabilityIframe } from './parts/NotifyAvailabilityIframe';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { AdaptiveDialog } from '@/components/blocks/AdaptiveDialog';

type Props = {
	family: string;
	gender: string;
	partNumber: string;
};

type GenerateIframeUrl = (args: {
	surveyUrl: string;
	family: string;
	gender: string;
	partNumber: string;
	storeId: string;
}) => string;

const generateIframeUrl: GenerateIframeUrl = ({
	surveyUrl,
	family,
	gender,
	partNumber,
	storeId,
}) => {
	const cleanGender = gender.replace('ñ', 'n');
	return `${surveyUrl}${partNumber}/${storeId}/${cleanGender}-${family}`;
};

export const NotifyAvailability: FC<Props> = ({ family, gender, partNumber }) => {
	const { showSuccessMessage, showErrorMessage } = useNotifications();
	const openDrawerNotify = useIsOpen();
	const { settings } = useSingleSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(useSettings().settings, router);

	const iframeSrc = useMemo(() => {
		const surveyUrl = settings?.userData['com.encuesta.tallasv2.url'];

		if (!surveyUrl) {
			return undefined;
		}

		return generateIframeUrl({
			surveyUrl,
			family,
			gender,
			partNumber,
			storeId,
		});
	}, [family, gender, partNumber, settings?.userData, storeId]);

	useEffect(() => {
		const handleIframeMessage = (event: MessageEvent) => {
			const status = getValueByPathFromUnknown(event, ['data', 'status']);

			switch (status) {
				case 'success':
					showSuccessMessage(
						'GRACIAS, Estaremos atentos para informarte cuando este producto lo tengamos disponible nuevamente. ¡No te pierdas todas las novedades que tenemos para ti!'
					);
					openDrawerNotify.off();
					break;
				case 'error':
					showErrorMessage('Ocurrió un error, intenta nuevamente más tarde.');
					openDrawerNotify.off();
					break;
				default:
					break;
			}
		};

		window.addEventListener('message', handleIframeMessage);

		return () => {
			window.removeEventListener('message', handleIframeMessage);
		};
	}, [showErrorMessage, showSuccessMessage, openDrawerNotify]);

	return (
		<React.Fragment>
			<Stack sx={{ marginTop: '10px !important' }}>
				<Typography sx={{ display: 'flex', gap: '1px', alignItems: 'center' }}>
					<NotificationsIcon
						sx={{ color: '#333333', fontSize: 'var(--font-size-default)', marginRight: '2px' }}
					/>
					<Typography sx={tileDescription2SX}>{'¿Buscas otra talla?'}</Typography>
					<Linkable href="" type="inline" sx={linkTitleSX} onClick={openDrawerNotify.on}>
						{'Notificarme disponibilidad'}
					</Linkable>
				</Typography>
			</Stack>
			<AdaptiveDialog
				title="ENCONTRAR MI TALLA"
				open={openDrawerNotify.open}
				onClose={openDrawerNotify.off}
			>
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						paddingBottom: '40px',
						height: { md: '400px', xs: '390px' },
					}}
				>
					<NotifyAvailabilityIframe src={iframeSrc} />
				</Stack>
			</AdaptiveDialog>
		</React.Fragment>
	);
};
